<script setup lang="ts">
// Imports
import { ref, onMounted } from 'vue';
import { NakedImage } from 'vue-datocms';
import { getMuxThumbnail } from '~/utils/GetMuxThumbnail';

// Types
import type { HeaderMediaFragment } from '#gql';

const props = defineProps<{
  asset: HeaderMediaFragment['asset'];
}>();

const videoRef = ref<HTMLVideoElement | null>(null);

const imageStyle = {
  maxWidth: '100%',
  height: '100%',
  objectFit: 'cover',
};

// Get poster thumbnail from Mux
const posterUrl = computed(() => {
  if (props.asset?.video?.muxPlaybackId) {
    return getMuxThumbnail(props.asset.video.muxPlaybackId, { width: 1920 });
  }
  return undefined;
});

onMounted(() => {
  const video = videoRef.value;
  if (!video) return;

  const startPlayback = () => {
    video.play().catch(() => undefined);
  };

  // readyState 4 = HAVE_ENOUGH_DATA (can play through)
  // readyState 3 = HAVE_FUTURE_DATA (can play current frame)
  if (video.readyState >= 3) {
    startPlayback();
  } else {
    video.addEventListener('canplay', startPlayback, { once: true });
  }
});
</script>

<template>
  <div class="asset-container">
    <video
      v-if="asset?.mimeType.includes('video') && asset.video?.mp4Url"
      ref="videoRef"
      class="video"
      :src="asset.video.mp4Url"
      :poster="posterUrl"
      muted
      loop
      playsinline
      preload="auto"
    />
    <template v-else-if="asset?.mimeType.includes('image')">
      <NakedImage
        v-if="asset.imageLandscape"
        :data="asset.imageLandscape"
        :img-class="'_hidden-vs'"
        :img-style="imageStyle"
      />
      <NakedImage
        v-if="asset.imagePortrait"
        :data="asset.imagePortrait"
        :img-class="'_hidden-vl'"
        :img-style="imageStyle"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.asset-container,
.asset-container > .video {
  width: 100%;
  height: 100%;
}

.asset-container > .video {
  object-fit: cover;
}
</style>
